import React from "react";
import {
  Icon,
  Text,
  toastManager,
  type Color,
  type GlyphName,
} from "@salestrak/ui";

import { AppearanceMenuRow } from "#app/components/menus/appearance-menu-row";
import { NO_PERMISSION } from "#app/components/menus/disabled-reason";
import {
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { useWorkspace, type WorkspaceTeam } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { resolveTeamGlyph } from "#app/lib/hooks/use-team-detail";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { copyText } from "#app/lib/utils/copy-actions";
import { teamNameError } from "#app/lib/team-handle";
import { AnalyticsContext, TeamEvent, trackEvent } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import { teamHref } from "#app/lib/utils/entity-hrefs";

/**
 * THE team action menu — the roster behind the team masthead crumb's
 * right-click: rename, copy link, appearance, settings. Hosts differ only in
 * menu kind (`parts`) and in where their rename affordance lives; the
 * workflow roster (`renderWorkflowMenuItems`) is the sibling grammar.
 *
 * The gate is the team's OWN `viewerCanManage` (manage.teams OR manager of
 * this team — server-computed, never re-derived), not a workspace-wide key,
 * so a team manager edits their team here exactly as they do on its settings
 * page. Rows the viewer cannot use are DISABLED with a reason, never hidden.
 */

/** The fields the menu reads. The workspace team row satisfies it. */
type TeamMenuTarget = Pick<
  WorkspaceTeam,
  "teamId" | "teamName" | "icon" | "viewerCanManage"
>;

/**
 * The shared handlers behind the menu, wired ONCE per hosting screen (never
 * per row — the mutation hook lives here). `updateTeam` refreshes the
 * workspace team list the crumb, the sidebar rows and the tables read.
 */
export function useTeamMenuActions() {
  const { activeWorkspace } = useWorkspace();
  const { push } = useAppNavigate();
  const { updateTeam } = useTeamMutations();

  const urlPath = activeWorkspace?.urlPath ?? "";

  return React.useMemo(
    () => ({
      copyLink(teamId: string) {
        void copyText(teamHref(urlPath, teamId), "Link").then((ok) => {
          if (ok) trackEvent({ context: AnalyticsContext.Team, event: TeamEvent.CopyLink });
        });
      },
      /** Appearance edits live-save the full pair — `icon` replaces the whole
       *  IconInput, so a color pick must re-send the glyph and vice versa.
       *  `icon: null` is the monogram (the name's initial on the tile). */
      async saveAppearance(teamId: string, icon: GlyphName | null, color: Color) {
        await updateTeam(teamId, { icon: { icon, color } }, { autosave: true });
      },
      // An inline rename has no field to hold an error under, so a name the
      // rules refuse is said in a toast and the crumb keeps the saved name.
      async rename(teamId: string, teamName: string) {
        const ruleError = teamNameError(teamName);
        if (ruleError) {
          toastManager.add({ title: `Team not renamed. ${ruleError}`, type: "error" });
          return;
        }
        await updateTeam(teamId, { teamName: teamName.trim() }, { autosave: true });
      },
      openSettings(teamId: string) {
        push(`/settings/teams/${teamId}`);
      },
    }),
    [urlPath, updateTeam, push],
  );
}

type TeamMenuActions = ReturnType<typeof useTeamMenuActions>;

type TeamMenuItemsProps = Omittable<{
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: MenuParts;
  team: TeamMenuTarget;
  /** From `useTeamMenuActions`, called once per hosting screen. */
  actions: TeamMenuActions;
  /** Starts the host's rename affordance — inline where the row can swap to
   *  an input. */
  onRename: () => void;
}>;

export function renderTeamMenuItems({
  parts = DROPDOWN_MENU_PARTS,
  team,
  actions,
  onRename,
}: TeamMenuItemsProps) {
  const { Item, Separator } = parts;
  const teamId = team.teamId;
  const canManage = team.viewerCanManage;
  const glyph = resolveTeamGlyph(team);

  const gatedItem = (opts: { onClick: () => void; children: React.ReactNode }) => (
    <Item
      {...(canManage
        ? { onClick: opts.onClick }
        : { disabled: true, disabledReason: NO_PERMISSION })}
    >
      {opts.children}
    </Item>
  );

  return (
    <>
      {gatedItem({
        onClick: onRename,
        children: (
          <>
            <Icon color="muted" name="pencil" size="md" />
            <Text>Rename</Text>
          </>
        ),
      })}
      <Item onClick={() => actions.copyLink(teamId)}>
        <Icon color="muted" name="link" size="md" />
        <Text>Copy link</Text>
      </Item>
      <Separator />
      {/* Monogram mode: the grid leads with "Use initial", which reports
          `null`. Each write re-sends the other half verbatim so a color pick
          never converts a monogram team into a glyph team. */}
      <AppearanceMenuRow
        disabled={!canManage}
        panel={{
          color: glyph.color,
          icon: glyph.icon,
          monogram: team.teamName,
          onColorChange: (next) =>
            void actions.saveAppearance(teamId, glyph.icon, next),
          onIconChange: (next) =>
            void actions.saveAppearance(teamId, next, glyph.color),
          // Offered only while a glyph replaces the monogram.
          onReset:
            glyph.icon === null
              ? undefined
              : () => void actions.saveAppearance(teamId, null, glyph.color),
        }}
        parts={parts}
        target={{ kind: "team", team }}
      />
      <Separator />
      {gatedItem({
        onClick: () => actions.openSettings(teamId),
        children: (
          <>
            <Icon color="muted" name="settings" size="md" />
            <Text>Team settings</Text>
          </>
        ),
      })}
    </>
  );
}
